import { useGetInstitutionsList } from 'modules/data';
import { useI18n } from 'modules/i18n';
import { useCurrentPage } from 'modules/routes';
import { ResetFiltersButton, Switch } from 'modules/ui';
import { useSearchParams } from 'react-router-dom';

import {
    useCatalogProjects,
    useExplorerInterviewRange,
    useExplorerProjectInstitutions,
    useProjectsAndCollectionsRange,
} from '../hooks';
import {
    FILTER_PARAMS,
    applyCollectionRangeParams,
    applyInstitutionLevelParam,
    applyInstitutionParam,
    applyInterviewRangeParams,
    applyQueryParam,
    resetExplorerFilters,
} from '../utils';
import { ExplorerInstitutionFilter } from './ExplorerInstitutionFilter';
import { ExplorerInstitutionLevelFilter } from './ExplorerInstitutionLevelFilter';
import { ExplorerRangeFilter } from './ExplorerRangeFilter';
import { ExplorerSearchInput } from './ExplorerSearchInput';

/**
 * Displays catalog filters and writes changes to the URL query parameters.
 * The sidebar stays mounted on other pages, where project fetching is disabled
 * and no filter UI is rendered. Institution fetching remains a separate SWR read.
 *
 * @returns {import('react').ReactElement|null} Catalog filters, or null outside list views.
 */
export function ExplorerSidebarSearch() {
    const { t } = useI18n();
    const currentPage = useCurrentPage();
    const [searchParams, setSearchParams] = useSearchParams();
    const { institutions: institutionsList } = useGetInstitutionsList({
        all: true,
    });

    const isCatalogPage = currentPage.pageType === 'catalog_page';
    const isCatalogRoot =
        isCatalogPage && currentPage.params.catalogType === 'root';
    const isInstitutionsIndex =
        isCatalogPage &&
        currentPage.params.catalogType === 'institutions' &&
        !currentPage.params.id;
    const showSidebarFilters = isCatalogRoot || isInstitutionsIndex;
    const { projects } = useCatalogProjects({ enabled: showSidebarFilters });

    const isInstitutionsList =
        isCatalogPage && currentPage.params.catalogType === 'institutions';
    const isProjectsList = !isInstitutionsList;

    const showUnpublished =
        searchParams.get('explorer_show_unpublished') === 'true';
    const hasUnpublishedProjects = projects.some(
        (project) => project.workflow_state === 'unshared'
    );
    const visibleProjects = showUnpublished
        ? projects
        : projects.filter((project) => project.workflow_state === 'public');

    const projectInstitutions = useExplorerProjectInstitutions({
        projects: visibleProjects,
    });
    const { globalMin, globalMax } = useExplorerInterviewRange({
        items: isProjectsList ? visibleProjects : (institutionsList ?? []),
        countField: isProjectsList ? 'interview_counts' : 'interviews',
    });
    const { globalCollectionMin, globalCollectionMax } =
        useProjectsAndCollectionsRange({ items: visibleProjects });

    const query = searchParams.get('explorer_q') || '';
    const interviewMin = searchParams.has('explorer_interviews_min')
        ? Number(searchParams.get('explorer_interviews_min'))
        : globalMin;
    const interviewMax = searchParams.has('explorer_interviews_max')
        ? Number(searchParams.get('explorer_interviews_max'))
        : globalMax;
    const collectionMin = searchParams.has('explorer_collections_min')
        ? Number(searchParams.get('explorer_collections_min'))
        : globalCollectionMin;
    const collectionMax = searchParams.has('explorer_collections_max')
        ? Number(searchParams.get('explorer_collections_max'))
        : globalCollectionMax;
    const institutionIds = searchParams.has('explorer_institution')
        ? searchParams
              .get('explorer_institution')
              .split(',')
              .map(Number)
              .filter(Boolean)
        : [];
    const institutionLevelParam = searchParams.get(
        'explorer_institution_level'
    );
    const institutionLevel = ['top_level'].includes(institutionLevelParam)
        ? institutionLevelParam
        : 'all';

    const titleLabel = isProjectsList
        ? 'modules.sidebar.catalog'
        : 'activerecord.models.institution.other';

    if (!showSidebarFilters) return null;

    const handleQueryChange = (e) =>
        setSearchParams((prev) => applyQueryParam(prev, e.target.value), {
            replace: true,
        });

    const handleClear = () =>
        setSearchParams((prev) => applyQueryParam(prev, ''), { replace: true });

    const handleInterviewRangeChange = ([min, max]) =>
        setSearchParams(
            (prev) =>
                applyInterviewRangeParams(prev, min, max, globalMin, globalMax),
            { replace: true }
        );

    const handleCollectionRangeChange = ([min, max]) =>
        setSearchParams(
            (prev) =>
                applyCollectionRangeParams(
                    prev,
                    min,
                    max,
                    globalCollectionMin,
                    globalCollectionMax
                ),
            { replace: true }
        );

    const handleInstitutionChange = (id) => {
        const next = institutionIds.includes(id)
            ? institutionIds.filter((x) => x !== id)
            : [...institutionIds, id];
        setSearchParams((prev) => applyInstitutionParam(prev, next), {
            replace: true,
        });
    };

    const handleInstitutionClearAll = () =>
        setSearchParams((prev) => applyInstitutionParam(prev, []), {
            replace: true,
        });

    const handleInstitutionLevelChange = (e) =>
        setSearchParams(
            (prev) => applyInstitutionLevelParam(prev, e.target.value),
            {
                replace: true,
            }
        );

    const hasActiveFilters = FILTER_PARAMS.some((key) => searchParams.has(key));
    const searchPlaceholderKey = isProjectsList
        ? 'explorer.search_placeholder.projects'
        : 'explorer.search_placeholder.institutions';

    /**
     * Updates the publication filter while preserving other catalog filters.
     *
     * @param {boolean} checked - Whether to include unpublished projects.
     * @returns {void}
     */
    const handleShowUnpublishedChange = (checked) => {
        setSearchParams(
            (prev) => {
                if (checked) {
                    prev.set('explorer_show_unpublished', 'true');
                } else {
                    prev.delete('explorer_show_unpublished');
                }
                return prev;
            },
            { replace: true }
        );
    };

    const handleResetAll = () =>
        setSearchParams((prev) => resetExplorerFilters(prev), {
            replace: true,
        });

    return (
        <>
            <h3 className="SidebarTabs-title">{t(titleLabel)}</h3>
            <div className="ExplorerSidebarSearch">
                {hasActiveFilters && (
                    <ResetFiltersButton onClick={handleResetAll} />
                )}
                <ExplorerSearchInput
                    value={query}
                    onChange={handleQueryChange}
                    onClear={handleClear}
                    placeholderKey={searchPlaceholderKey}
                />

                {isProjectsList && hasUnpublishedProjects && (
                    <div className="ExplorerSidebarSearch-rangeSection">
                        <Switch
                            data-testid="show-unpublished-projects"
                            checked={showUnpublished}
                            onChange={handleShowUnpublishedChange}
                            label={t(
                                'explorer.filter.show_unpublished_projects'
                            )}
                        />
                    </div>
                )}

                <ExplorerRangeFilter
                    label={t('explorer.filter.interviews')}
                    globalMin={globalMin}
                    globalMax={globalMax}
                    value={[interviewMin, interviewMax]}
                    onChange={handleInterviewRangeChange}
                />

                {isProjectsList && (
                    <ExplorerRangeFilter
                        label={t('explorer.filter.collections')}
                        globalMin={globalCollectionMin}
                        globalMax={globalCollectionMax}
                        value={[collectionMin, collectionMax]}
                        onChange={handleCollectionRangeChange}
                    />
                )}

                {isProjectsList && (
                    <ExplorerInstitutionFilter
                        institutions={projectInstitutions}
                        values={institutionIds}
                        onChange={handleInstitutionChange}
                        onClearAll={handleInstitutionClearAll}
                    />
                )}

                {!isProjectsList && (
                    <ExplorerInstitutionLevelFilter
                        value={institutionLevel}
                        onChange={handleInstitutionLevelChange}
                    />
                )}
            </div>
        </>
    );
}

export default ExplorerSidebarSearch;
